import { useEffect, useState } from "react";
import { Alert, Form, Input, InputNumber, Modal, Select } from "antd";
import { rtpApi, runRtpWrite } from "./rtpHooks";
import type { RtpPhase, RtpPlanDetail } from "./types";

const { TextArea } = Input;

interface PhaseModalProps {
	plan: RtpPlanDetail;
	phase: RtpPhase | null;
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

interface PhaseFormValues {
	name: string;
	goal?: string;
	plannedDurationDays: number;
	order?: number;
}

// Add mode: ADD_PHASE rejects an insert before the active phase, so the lowest selectable
// position is the active phase's order on an active plan (1 otherwise) — see §3 ADD_PHASE.
function getMinPosition(plan: RtpPlanDetail): number {
	if (plan.status !== "active") return 1;
	const activePhase = plan.phases.find((p) => p.id === plan.activePhaseId);
	return activePhase?.order ?? 1;
}

function getPositionOptions(plan: RtpPlanDetail): { value: number; label: string }[] {
	const minPosition = getMinPosition(plan);
	const maxPosition = plan.phases.length + 1;
	const options: { value: number; label: string }[] = [];
	for (let position = minPosition; position <= maxPosition; position++) {
		const occupant = plan.phases.find((p) => p.order === position);
		options.push({
			value: position,
			label: occupant ? `Before "${occupant.name}"` : "At the end",
		});
	}
	return options;
}

export const PhaseModal = ({ plan, phase, open, onClose, refetch }: PhaseModalProps) => {
	const [form] = Form.useForm<PhaseFormValues>();
	const [submitting, setSubmitting] = useState(false);
	const isEdit = phase != null;

	useEffect(() => {
		if (!open) return;
		form.setFieldsValue(
			phase
				? { name: phase.name, goal: phase.goal, plannedDurationDays: phase.plannedDurationDays }
				: { name: undefined, goal: undefined, plannedDurationDays: 7, order: undefined }
		);
		// Re-seed only when the modal opens (or the target phase changes) — not on every plan
		// refetch while it's already open, which would clobber in-progress edits.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, phase]);

	const handleClose = () => {
		onClose();
		form.resetFields();
	};

	const handleSubmit = async () => {
		let values: PhaseFormValues;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}
		setSubmitting(true);
		const write = phase
			? () =>
					rtpApi.patchRtpPhase(phase.id, {
						name: values.name,
						goal: values.goal,
						plannedDurationDays: values.plannedDurationDays,
					})
			: () =>
					rtpApi.rtpPlanAction(plan.id, {
						action: "ADD_PHASE",
						name: values.name,
						goal: values.goal,
						plannedDurationDays: values.plannedDurationDays,
						...(values.order != null ? { order: values.order } : {}),
					});
		const ok = await runRtpWrite(write, refetch);
		setSubmitting(false);
		if (ok) handleClose();
	};

	return (
		<Modal
			title={isEdit ? "Edit Phase" : "Add Phase"}
			open={open}
			onCancel={handleClose}
			onOk={handleSubmit}
			confirmLoading={submitting}
			destroyOnHidden
			okButtonProps={{ "data-attr": "settings.rtp-phase.submit" } as any}
		>
			<Form form={form} layout="vertical">
				<Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: "Enter a name" }]}>
					<Input placeholder="Phase name" />
				</Form.Item>
				<Form.Item name="goal" label="Goal">
					<TextArea rows={2} placeholder="Optional goal" />
				</Form.Item>
				<Form.Item
					name="plannedDurationDays"
					label="Planned duration (days)"
					rules={[{ required: true, message: "Enter a duration" }]}
				>
					<InputNumber min={1} style={{ width: "100%" }} />
				</Form.Item>
				{!isEdit ? (
					<Form.Item name="order" label="Position">
						<Select
							allowClear
							placeholder="At the end (default)"
							options={getPositionOptions(plan)}
						/>
					</Form.Item>
				) : null}
				{!isEdit && plan.status === "active" ? (
					<Alert
						type="info"
						showIcon
						message="Inserting at the active phase's position makes it the new active phase."
						style={{ marginBottom: 16 }}
					/>
				) : null}
			</Form>
		</Modal>
	);
};
